import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ClientOnly } from "@tanstack/react-router";
import {
  Fingerprint,
  KeyRound,
  ShieldAlert,
  ShieldCheck,
  Copy,
  Check,
  Eye,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { useWallet } from "@/lib/wallet/store";
import logo from "@/assets/cobalt-co-logo.png";
import { passkeysAvailable, cancelBiometricPrompt } from "@/lib/wallet/vault";
import {
  createMnemonic,
  isValidMnemonic,
  mnemonicWords,
  unknownWords,
  type MnemonicLength,
} from "@/lib/wallet/mnemonic";
import { truncateAddress } from "@/lib/wallet/format";
import { AppShell } from "./AppShell";
import { SiteFooter } from "./SiteFooter";

/**
 * Everything behind the lock renders only when the vault is open. This is the
 * single choke point: no route reads ledger data or exposes actions unlocked.
 */
export function Gate({ children }: { children: ReactNode }) {
  return (
    <ClientOnly fallback={<AppShell><LoadingPane /></AppShell>}>
      <GateInner>{children}</GateInner>
    </ClientOnly>
  );
}

function LoadingPane() {
  return (
    <div className="panel animate-rise grid h-40 place-items-center">
      <p className="label-instrument">Initializing secure context…</p>
    </div>
  );
}

function GateInner({ children }: { children: ReactNode }) {
  const { hydrated, vault, unlocked, pendingBackup } = useWallet();

  if (!hydrated) return <AppShell><LoadingPane /></AppShell>;
  if (!vault) return <AppShell><Onboarding /></AppShell>;
  if (!unlocked) return <AppShell><Unlock /></AppShell>;
  if (pendingBackup) return <AppShell><BackupPhrase /></AppShell>;
  return <AppShell>{children}</AppShell>;
}

/* ------------------------------------------------------------- onboarding */

const ACKS = [
  {
    id: "custody",
    text: "I hold my own keys. Cobalt Wallet has no server, no account and no way to reset access for me.",
  },
  {
    id: "backup",
    text: "My recovery phrase is the only backup. I will write it on paper, offline, and never photograph or type it into another app.",
  },
  {
    id: "irreversible",
    text: "XRPL payments are final. A wrong address or missing destination tag cannot be reversed.",
  },
  {
    id: "extra",
    text: "If I add a 25th word, losing it means losing the account even with all 24 words.",
  },
] as const;

type Step = "learn" | "choose" | "phrase" | "protect" | "import";

function Onboarding() {
  const { createFromMnemonic, importMnemonic, importSeed, enableBiometrics, biometricsSupported } =
    useWallet();
  const [step, setStep] = useState<Step>("choose");
  // Which path the security briefing leads into once every point is confirmed.
  const [intent, setIntent] = useState<"create" | "import">("create");
  const [acked, setAcked] = useState<string[]>([]);
  const [length, setLength] = useState<MnemonicLength>(24);
  const [phrase, setPhrase] = useState("");
  const [extraWord, setExtraWord] = useState("");
  const [useExtra, setUseExtra] = useState(false);
  const [extraConfirm, setExtraConfirm] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const [importText, setImportText] = useState("");
  const [importKind, setImportKind] = useState<"phrase" | "seed">("phrase");
  const [wordCount, setWordCount] = useState<12 | 24>(24);
  const [wordInputs, setWordInputs] = useState<string[]>(() => Array<string>(24).fill(""));
  const wordRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [busy, setBusy] = useState(false);

  const allAcked = acked.length === ACKS.length;
  const words = useMemo(() => mnemonicWords(phrase), [phrase]);
  const importPhrase = useMemo(
    () =>
      wordInputs
        .slice(0, wordCount)
        .map((w) => w.trim().toLowerCase())
        .filter(Boolean)
        .join(" "),
    [wordInputs, wordCount],
  );

  const setWordAt = (i: number, raw: string) => {
    const parts = raw.trim().toLowerCase().split(/\s+/).filter(Boolean);
    setWordInputs((prev) => {
      const next = [...prev];
      if (parts.length > 1) {
        // Pasted multiple words: fill forward from this slot.
        parts.forEach((p, k) => {
          if (i + k < next.length) next[i + k] = p;
        });
      } else {
        next[i] = parts[0] ?? "";
      }
      return next;
    });
    if (parts.length > 1) {
      const target = Math.min(i + parts.length, wordCount - 1);
      wordRefs.current[target]?.focus();
    } else if (raw.endsWith(" ") && i + 1 < wordCount) {
      wordRefs.current[i + 1]?.focus();
    }
  };

  const setWordTotal = (n: 12 | 24) => {
    setWordCount(n);
    setWordInputs(Array<string>(n).fill(""));
    wordRefs.current = [];
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const generate = (len: MnemonicLength) => {
    setLength(len);
    setPhrase(createMnemonic(len));
    setRevealed(false);
  };

  const startPhrase = () => {
    generate(length);
    setStep("phrase");
  };

  /** Both first-run paths pass through the briefing before any key material. */
  const beginFlow = (next: "create" | "import") => {
    setIntent(next);
    if (next === "import") setImportKind("phrase");
    setStep("learn");
  };

  const continueFromLearn = () => {
    if (intent === "import") {
      setStep("import");
      return;
    }
    startPhrase();
  };

  const goProtect = () => {
    if (useExtra) {
      if (!extraWord.trim()) {
        toast.error("Enter your 25th word or turn the option off.");
        return;
      }
      if (extraWord.trim() !== extraConfirm.trim()) {
        toast.error("The 25th word entries do not match.");
        return;
      }
    }
    setStep("protect");
  };

  /**
   * Enrols device biometrics right after the vault is created so the passcode
   * becomes the backup path rather than the everyday one. Never fatal.
   */
  const offerBiometrics = async () => {
    if (!biometricsSupported) return;
    try {
      await enableBiometrics();
      toast.success("Biometric unlock enabled on this device.");
    } catch (err) {
      toast.message(
        err instanceof Error ? err.message : "Biometric unlock is unavailable on this device.",
      );
    }
  };

  const finishCreate = (protection: "passkey" | "passphrase") =>
    run(async () => {
      await createFromMnemonic({
        mnemonic: phrase,
        ...(useExtra && extraWord.trim() ? { extraWord: extraWord.trim() } : {}),
        protection,
        ...(protection === "passphrase" ? { passphrase } : {}),
      });
      if (protection === "passphrase") await offerBiometrics();
    });

  const finishImport = (protection: "passkey" | "passphrase") =>
    run(async () => {
      if (importKind === "seed") {
        await importSeed(importText.trim(), protection, passphrase);
        if (protection === "passphrase") await offerBiometrics();
        return;
      }
      await importMnemonic({
        mnemonic: importPhrase,
        ...(useExtra && extraWord.trim() ? { extraWord: extraWord.trim() } : {}),
        protection,
        ...(protection === "passphrase" ? { passphrase } : {}),
      });
      if (protection === "passphrase") await offerBiometrics();
    });

  if (step === "choose") {
    return (
      <div className="animate-rise flex min-h-[70vh] flex-col justify-center space-y-8">
        <div className="text-center">
          <img
            src={logo}
            alt="Cobalt Wallet"
            width={1024}
            height={1024}
            className="mx-auto size-32 object-contain"
          />
          <h1 className="mt-5 text-[24px] font-medium leading-tight tracking-tight">
            Cobalt Wallet<span className="sr-only"> — passkey-secured XRPL wallet</span>
          </h1>
          <p className="readout mt-2 text-[11px] leading-relaxed text-muted">
            Self-custody XRPL. Keys are generated and encrypted on this device only.
          </p>
        </div>

        <div className="space-y-3">
          <button
            type="button"
            onClick={() => beginFlow("create")}
            className="btn-accent h-14 w-full text-[12px]"
          >
            Create a new wallet
          </button>
          <button
            type="button"
            onClick={() => beginFlow("import")}
            className="btn-outline-accent h-14 w-full text-[12px]"
          >
            Import an existing seed phrase
          </button>
        </div>

        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="animate-rise space-y-4">
      <section className="panel p-5">
        <span className="grid size-10 place-items-center rounded-lg accent-tint">
          <ShieldCheck className="size-5" />
        </span>
        <p className="label-instrument mt-3">
          {step === "learn"
            ? "Security model"
            : step === "import"
              ? "Restore"
              : step === "phrase"
                ? "Recovery phrase"
                : "Device protection"}
        </p>
        <h1 className="mt-2 text-[22px] font-medium leading-tight tracking-tight">
          {step === "learn"
            ? "Read this before you hold your own keys"
            : step === "import"
              ? "Import an existing seed phrase"
              : step === "phrase"
                ? `Your ${length}-word recovery phrase`
                : "Lock this device"}
        </h1>
        <p className="readout mt-2 text-[11px] leading-relaxed text-muted">
          Keys are generated here, encrypted here, and never sent anywhere. There is no server, no
          account and no recovery desk — your recovery phrase is the only recovery path.
        </p>
      </section>

      {step === "learn" ? (
        <section className="panel space-y-3 p-4">
          {ACKS.map((ack) => {
            const on = acked.includes(ack.id);
            return (
              <button
                key={ack.id}
                type="button"
                onClick={() =>
                  setAcked((prev) => (on ? prev.filter((i) => i !== ack.id) : [...prev, ack.id]))
                }
                className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                  on ? "chip-active" : "border-border text-muted hover:bg-surface2"
                }`}
              >
                <span
                  className={`mt-0.5 grid size-4 shrink-0 place-items-center rounded-[4px] border ${
                    on ? "border-accent bg-accent text-accent-ink" : "border-border"
                  }`}
                >
                  {on ? <Check className="size-3" /> : null}
                </span>
                <span className="readout text-[10px] leading-relaxed">{ack.text}</span>
              </button>
            );
          })}
          <button
            type="button"
            disabled={!allAcked}
            onClick={continueFromLearn}
            className="btn-accent h-11 w-full disabled:opacity-40"
          >
            {allAcked
              ? intent === "import"
                ? "I understand — import my phrase"
                : "I understand — generate my phrase"
              : `Confirm all ${ACKS.length} points`}
          </button>
          <button
            type="button"
            onClick={() => setStep("choose")}
            className="readout h-9 w-full text-[10px] uppercase tracking-[0.15em] text-faint"
          >
            Back
          </button>
        </section>
      ) : null}


      {step === "phrase" ? (
        <>
          <section className="panel space-y-3 p-4">
            <div className="flex gap-2">
              {([12, 24] as MnemonicLength[]).map((len) => (
                <button
                  key={len}
                  type="button"
                  onClick={() => generate(len)}
                  className={`readout h-9 flex-1 rounded-lg border text-[11px] uppercase tracking-[0.15em] ${
                    length === len ? "chip-active" : "border-border text-muted"
                  }`}
                >
                  {len} words
                </button>
              ))}
            </div>

            <div className="relative">
              <div
                className={`grid grid-cols-3 gap-1.5 ${revealed ? "" : "blur-sm select-none"}`}
                aria-hidden={!revealed}
              >
                {words.map((w, i) => (
                  <div
                    key={`${w}-${i}`}
                    className="readout flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-[11px]"
                  >
                    <span className="text-faint">{i + 1}</span>
                    <span className="truncate">{w}</span>
                  </div>
                ))}
              </div>
              {!revealed ? (
                <button
                  type="button"
                  onClick={() => setRevealed(true)}
                  className="absolute inset-0 grid place-items-center"
                >
                  <span className="btn-accent flex h-9 items-center gap-2 px-4">
                    <Eye className="size-3" /> Reveal
                  </span>
                </button>
              ) : null}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRevealed((r) => !r)}
                className="btn-quiet flex h-9 flex-1 items-center justify-center gap-2"
              >
                {revealed ? <EyeOff className="size-3" /> : <Eye className="size-3" />}
                {revealed ? "Hide" : "Reveal"}
              </button>
              <button
                type="button"
                onClick={() => generate(length)}
                className="btn-quiet flex h-9 flex-1 items-center justify-center gap-2"
              >
                <RefreshCw className="size-3" /> New phrase
              </button>
            </div>
            <p className="readout text-[10px] leading-relaxed text-faint">
              Write these words down in order, on paper. They are shown again on the next screen and
              then never again.
            </p>
          </section>

          <section className="panel space-y-3 p-4">
            <button
              type="button"
              onClick={() => setUseExtra((v) => !v)}
              className="flex w-full items-start gap-3 text-left"
            >
              <span
                className={`mt-0.5 grid size-4 shrink-0 place-items-center rounded-[4px] border ${
                  useExtra ? "border-accent bg-accent text-accent-ink" : "border-border"
                }`}
              >
                {useExtra ? <Check className="size-3" /> : null}
              </span>
              <span>
                <span className="block text-[13px] font-medium">Add a 25th word</span>
                <span className="readout mt-1 block text-[10px] leading-relaxed text-faint">
                  A secret you choose, never written on the paper backup. A stolen phrase alone then
                  opens nothing — but if you forget it, the account is unrecoverable.
                </span>
              </span>
            </button>
            {useExtra ? (
              <>
                <input
                  type="password"
                  value={extraWord}
                  autoComplete="off"
                  onChange={(e) => setExtraWord(e.target.value)}
                  placeholder="25th word"
                  className="field h-11 w-full px-3"
                />
                <input
                  type="password"
                  value={extraConfirm}
                  autoComplete="off"
                  onChange={(e) => setExtraConfirm(e.target.value)}
                  placeholder="Repeat 25th word"
                  className="field h-11 w-full px-3"
                />
              </>
            ) : null}
          </section>

          <div className="flex gap-2">
            <button type="button" onClick={() => setStep("choose")} className="btn-quiet h-11 flex-1">
              Back
            </button>
            <button
              type="button"
              disabled={!isValidMnemonic(phrase)}
              onClick={goProtect}
              className="btn-accent h-11 flex-[2] disabled:opacity-40"
            >
              Continue
            </button>
          </div>
        </>
      ) : null}

      {step === "protect" || step === "import" ? (
        <section className="panel space-y-3 p-4">
          {step === "import" ? (
            <>
              <div className="flex gap-2">
                {(["phrase", "seed"] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setImportKind(k)}
                    className={`readout h-9 flex-1 rounded-lg border text-[11px] uppercase tracking-[0.15em] ${
                      importKind === k ? "chip-active" : "border-border text-muted"
                    }`}
                  >
                    {k === "phrase" ? "Recovery phrase" : "Family seed"}
                  </button>
                ))}
              </div>
              {importKind === "phrase" ? (
                <>
                  <div className="flex gap-2">
                    {([12, 24] as const).map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setWordTotal(n)}
                        className={`readout h-8 flex-1 rounded-lg border text-[11px] uppercase tracking-[0.15em] ${
                          wordCount === n ? "chip-active" : "border-border text-muted"
                        }`}
                      >
                        {n} words
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {Array.from({ length: wordCount }, (_, i) => (
                      <label
                        key={i}
                        className="field flex h-10 items-center gap-1.5 rounded-xl px-2 focus-within:border-accent"
                      >
                        <span className="readout w-4 shrink-0 text-right text-[9px] text-faint">
                          {i + 1}
                        </span>
                        <input
                          ref={(el) => {
                            wordRefs.current[i] = el;
                          }}
                          value={wordInputs[i] ?? ""}
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck={false}
                          onChange={(e) => setWordAt(i, e.target.value)}
                          onKeyDown={(e) => {
                            if (
                              e.key === "Backspace" &&
                              !(wordInputs[i] ?? "") &&
                              i > 0
                            ) {
                              wordRefs.current[i - 1]?.focus();
                            }
                          }}
                          className="readout w-full min-w-0 bg-transparent text-[12px] text-ink outline-none"
                        />
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                <textarea
                  value={importText}
                  rows={2}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder="s…"
                  className="field w-full resize-none p-3 text-[12px]"
                />
              )}
              {importKind === "phrase" && importPhrase ? (
                <p className="readout text-[10px] leading-relaxed text-faint">
                  {mnemonicWords(importPhrase).length} words ·{" "}
                  {unknownWords(importPhrase).length
                    ? `not in wordlist: ${unknownWords(importPhrase).slice(0, 3).join(", ")}`
                    : isValidMnemonic(importPhrase)
                      ? "checksum valid"
                      : "checksum not valid yet"}
                </p>
              ) : null}
              {importKind === "phrase" ? (
                <>
                  <button
                    type="button"
                    onClick={() => setUseExtra((v) => !v)}
                    className="readout flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-muted"
                  >
                    <span
                      className={`grid size-4 place-items-center rounded-[4px] border ${
                        useExtra ? "border-accent bg-accent text-accent-ink" : "border-border"
                      }`}
                    >
                      {useExtra ? <Check className="size-3" /> : null}
                    </span>
                    This phrase has a 25th word
                  </button>
                  {useExtra ? (
                    <input
                      type="password"
                      value={extraWord}
                      autoComplete="off"
                      onChange={(e) => setExtraWord(e.target.value)}
                      placeholder="25th word"
                      className="field h-11 w-full px-3"
                    />
                  ) : null}
                </>
              ) : null}
              <p className="readout text-[10px] leading-relaxed text-faint">
                Restore only on a device you trust. The secret is encrypted immediately and never
                leaves this browser.
              </p>
            </>
          ) : null}

          <p className="label-instrument pt-1">Protect this device</p>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={passphrase}
            autoComplete="new-password"
            onChange={(e) => setPassphrase(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="6-digit passcode"
            className="field h-11 w-full px-3 text-center tracking-[0.5em]"
          />
          <p className="readout text-[10px] leading-relaxed text-faint">
            Biometrics (Face ID / Touch ID / Windows Hello) unlock a hardware-derived key via
            WebAuthn PRF — this is the recommended everyday unlock. The 6-digit passcode is stretched
            with 600,000 PBKDF2 rounds into an AES-256-GCM key and stays as your backup way in.
            Either way the encrypted vault never leaves this device.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStep(step === "import" ? "choose" : "phrase")}
              className="btn-quiet h-10 flex-1"
            >
              Back
            </button>
            <button
              type="button"
              disabled={
                busy ||
                !passkeysAvailable() ||
                (step === "import" &&
                  !(importKind === "seed" ? importText.trim() : importPhrase))
              }
              onClick={() =>
                step === "import" ? void finishImport("passkey") : void finishCreate("passkey")
              }
              className="btn-outline-accent flex h-10 flex-1 items-center justify-center gap-2 disabled:opacity-40"
            >
              <Fingerprint className="size-3" /> Biometrics
            </button>
            <button
              type="button"
              disabled={
                busy ||
                passphrase.length !== 6 ||
                (step === "import" &&
                  !(importKind === "seed" ? importText.trim() : importPhrase))
              }
              onClick={() =>
                step === "import"
                  ? void finishImport("passphrase")
                  : void finishCreate("passphrase")
              }
              className="btn-accent h-10 flex-1 disabled:opacity-40"
            >
              Passcode
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ unlock */

function Unlock() {
  const { vault, unlock, unlockBiometric, biometricsEnabled, biometricsSupported } = useWallet();
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState(false);
  // Passcode vaults always show the keypad first so a stuck or unsupported
  // biometric prompt can never lock the user out.
  const [usePasscode, setUsePasscode] = useState(vault?.kind === "passphrase" || !biometricsEnabled);

  const biometric = async () => {
    setBusy(true);
    try {
      await unlockBiometric();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Biometric unlock failed.";
      if (msg !== "Biometric prompt cancelled.") toast.error(msg);
      if (vault?.kind === "passphrase") setUsePasscode(true);
    } finally {
      setBusy(false);
    }
  };

  const attempt = async () => {
    cancelBiometricPrompt();
    setBusy(true);
    try {
      await unlock(passphrase);
      setPassphrase("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unlock failed.");
      setPassphrase("");
    } finally {
      setBusy(false);
    }
  };

  const canEnrol = !biometricsEnabled && biometricsSupported;

  return (
    <div className="animate-rise space-y-4 pt-10">
      <div className="panel p-6 text-center">
        <span className="mx-auto grid size-14 place-items-center rounded-2xl accent-tint">
          {biometricsEnabled && !usePasscode ? (
            <Fingerprint className="size-7" />
          ) : (
            <KeyRound className="size-7" />
          )}
        </span>
        <h1 className="mt-4 text-[20px] font-medium tracking-tight">Vault locked</h1>
        <p className="readout mt-1 text-[11px] text-faint">
          {truncateAddress(vault?.address ?? "", 6, 6)}
        </p>

        {usePasscode && vault?.kind === "passphrase" ? (
          <>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={passphrase}
              autoComplete="current-password"
              onChange={(e) => setPassphrase(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => {
                if (e.key === "Enter") void attempt();
              }}
              placeholder="6-digit passcode"
              className="field mt-5 h-11 w-full px-3 text-center tracking-[0.5em]"
            />
            <button
              type="button"
              disabled={busy || passphrase.length !== 6}
              onClick={() => void attempt()}
              className="btn-accent mt-4 h-11 w-full disabled:opacity-40"
            >
              {busy ? "Verifying…" : "Unlock"}
            </button>
            {biometricsEnabled ? (
              <button
                type="button"
                onClick={() => {
                  setUsePasscode(false);
                  void biometric();
                }}
                className="readout mt-3 text-[10px] uppercase tracking-[0.15em] text-accent"
              >
                Use biometrics instead
              </button>
            ) : null}
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => void biometric()}
              className="btn-accent mt-5 flex h-11 w-full items-center justify-center gap-2 disabled:opacity-40"
            >
              <Fingerprint className="size-4" />
              {busy ? "Verifying…" : "Unlock with biometrics"}
            </button>
            {vault?.kind === "passphrase" ? (
              <button
                type="button"
                onClick={() => setUsePasscode(true)}
                className="readout mt-3 text-[10px] uppercase tracking-[0.15em] text-muted"
              >
                Use passcode instead
              </button>
            ) : null}
          </>
        )}
      </div>
      <p className="readout px-2 text-center text-[10px] leading-relaxed text-faint">
        {canEnrol
          ? "Biometric unlock is not enrolled yet on this device — unlock once with your passcode, then turn it on under Settings."
          : "The encrypted vault stays on this device. Biometrics release a hardware-held key; without it, or your passcode, the ciphertext is inert."}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ backup */

function BackupPhrase() {
  const { pendingBackup, confirmBackup } = useWallet();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stage, setStage] = useState<"show" | "verify">("show");
  const [answers, setAnswers] = useState<Record<number, string>>({});

  const words = useMemo(
    () => (pendingBackup?.mnemonic ? mnemonicWords(pendingBackup.mnemonic) : []),
    [pendingBackup?.mnemonic],
  );

  // Two fixed challenge positions per phrase, chosen once.
  const challenges = useMemo(() => {
    if (words.length === 0) return [] as number[];
    const a = Math.floor(Math.random() * words.length);
    let b = Math.floor(Math.random() * words.length);
    if (b === a) b = (a + 1) % words.length;
    return [a, b].sort((x, y) => x - y);
  }, [words]);

  if (!pendingBackup) return null;
  const hasPhrase = words.length > 0;
  const verified = challenges.every(
    (i) => (answers[i] ?? "").trim().toLowerCase() === words[i],
  );

  return (
    <div className="animate-rise space-y-4">
      <section className="panel p-5">
        <span className="grid size-10 place-items-center rounded-lg bg-danger/10 text-danger">
          <ShieldAlert className="size-5" />
        </span>
        <h1 className="mt-3 text-[20px] font-medium tracking-tight">
          {stage === "show" ? "Back up your recovery phrase" : "Confirm your backup"}
        </h1>
        <p className="readout mt-2 text-[11px] leading-relaxed text-muted">
          {stage === "show"
            ? "This is the only way to restore the account. Write it on paper. Anyone who sees it owns your funds. Cobalt Wallet has no copy and cannot help you recover it."
            : "Type the requested words from your paper backup. This is the last screen where the phrase exists on this device."}
          {pendingBackup.usedExtraWord
            ? " Your 25th word is not shown or stored anywhere — keep it separately."
            : ""}
        </p>
      </section>

      {stage === "show" ? (
        <section className="panel p-4">
          <p className="label-instrument">{hasPhrase ? "Recovery phrase" : "Family seed"}</p>
          {hasPhrase ? (
            <div className={`mt-2 grid grid-cols-3 gap-1.5 ${revealed ? "" : "blur-sm select-none"}`}>
              {words.map((w, i) => (
                <div
                  key={`${w}-${i}`}
                  className="readout flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-[11px]"
                >
                  <span className="text-faint">{i + 1}</span>
                  <span className="truncate">{w}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="readout relative mt-2 rounded-lg border border-border bg-background p-4 text-center text-[14px] break-all">
              <span className={revealed ? "" : "blur-sm select-none"}>{pendingBackup.seed}</span>
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setRevealed((r) => !r)}
              className="btn-quiet h-10 flex-1"
            >
              {revealed ? "Hide" : "Reveal"}
            </button>
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(pendingBackup.mnemonic ?? pendingBackup.seed);
                setCopied(true);
                toast.warning("Copied. Clear your clipboard when you're done.");
              }}
              className="btn-quiet flex h-10 flex-1 items-center justify-center gap-2"
            >
              {copied ? <Check className="size-3" /> : <Copy className="size-3" />} Copy
            </button>
          </div>
          <p className="readout mt-3 text-[10px] leading-relaxed text-faint">
            Account {truncateAddress(pendingBackup.address, 6, 6)} activates once it receives the
            network reserve in XRP.
          </p>
        </section>
      ) : (
        <section className="panel space-y-3 p-4">
          {challenges.map((i) => (
            <div key={i} className="space-y-1.5">
              <p className="label-instrument">Word {i + 1}</p>
              <input
                value={answers[i] ?? ""}
                autoComplete="off"
                autoCapitalize="none"
                onChange={(e) => setAnswers((prev) => ({ ...prev, [i]: e.target.value }))}
                className="field h-11 w-full px-3"
              />
            </div>
          ))}
        </section>
      )}

      {stage === "show" ? (
        <button
          type="button"
          onClick={() => (hasPhrase ? setStage("verify") : confirmBackup())}
          className="btn-accent h-11 w-full"
        >
          {hasPhrase ? "I wrote it down" : "I stored it offline"}
        </button>
      ) : (
        <div className="flex gap-2">
          <button type="button" onClick={() => setStage("show")} className="btn-quiet h-11 flex-1">
            Show phrase
          </button>
          <button
            type="button"
            disabled={!verified}
            onClick={confirmBackup}
            className="btn-accent h-11 flex-[2] disabled:opacity-40"
          >
            {verified ? "Confirm backup" : "Words must match"}
          </button>
        </div>
      )}
      <p className="readout px-2 text-center text-[10px] leading-relaxed text-faint">
        Never share these words. Support staff, airdrops and "verification" requests asking for them
        are always theft.
      </p>
    </div>
  );
}
